import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Space, 
  Typography, 
  Alert, 
  Tooltip,
  Tag,
  Row,
  Col
} from 'antd';
import { 
  ApiOutlined, 
  CopyOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined, 
  ReloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { siteApi } from '../../utils/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Composant pour gérer l'API E-commerce
 * Permet au site web d'afficher les produits et de créer des commandes
 */
const CustomStoreSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [siteInfo, setSiteInfo] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Charger les informations au montage du composant
  useEffect(() => {
    loadSiteInfo();
  }, []);

  /**
   * Charge les informations de la Site API
   */
  const loadSiteInfo = async () => {
    try {
      setLoadingInfo(true);
      const response = await siteApi.getSiteInfo();
      
      if (response?.data?.success) {
        const info = response.data.data;
        setSiteInfo(info);
        
        if (info) {
          form.setFieldsValue({
            siteDomain: info.siteDomain,
            siteName: info.siteName || ''
          });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations API:', error);
      if (error.response?.status !== 404) {
        message.error('Erreur lors du chargement des informations API');
      }
    } finally {
      setLoadingInfo(false);
    }
  };

  /**
   * Génère la clé API pour le site web
   */
  const handleGenerateKey = async (values) => {
    try {
      setLoading(true);
      const response = await siteApi.generateApiKey(
        values.siteDomain, 
        values.siteName || null
      );

      if (response?.data?.success) {
        const result = response.data.data;
        setSiteInfo(result);
        
        message.success(
          siteInfo 
            ? 'Nouvelle clé API générée avec succès !' 
            : 'Clé API créée avec succès !'
        );
      } else {
        throw new Error(response?.data?.message || 'Erreur inconnue');
      }
    } catch (error) {
      console.error('Erreur génération clé API:', error);
      message.error(
        error.response?.data?.message || 'Erreur lors de la génération de la clé API'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copie un texte dans le presse-papiers
   */
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} copiée !`);
    } catch (error) {
      message.error('Erreur lors de la copie');
    }
  };

  /**
   * Masque partiellement une clé
   */
  const maskKey = (key) => {
    if (!key || key.length <= 8) return key;
    return `${key.substring(0, 4)}${'*'.repeat(key.length - 8)}${key.substring(key.length - 4)}`;
  };

  if (loadingInfo) {
    return (
      <Card loading={true}>
        <div className="text-center py-8">
          Chargement des paramètres API...
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Alert
        message="API E-commerce pour votre site web"
        description="Cette API permet à votre site web d'afficher vos produits, catégories et de recevoir les commandes de vos clients."
        type="info"
        showIcon
      />

      {/* Configuration API */}
      <Card 
        title={
          <Space>
            <GlobalOutlined />
            Configuration API E-commerce
          </Space>
        }
        extra={
          siteInfo && (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Configurée
            </Tag>
          )
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerateKey}
          disabled={loading}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Domaine de votre site"
                name="siteDomain"
                rules={[
                  { required: true, message: 'Le domaine est requis' },
                  { 
                    pattern: /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 
                    message: 'Format de domaine invalide (ex: monsite.com)' 
                  }
                ]}
              >
                <Input 
                  placeholder="monsite.com"
                  prefix={<GlobalOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Nom de votre site (optionnel)"
                name="siteName"
              >
                <Input 
                  placeholder="Mon E-commerce"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={siteInfo ? <ReloadOutlined /> : <ApiOutlined />}
              size="large"
            >
              {siteInfo ? 'Régénérer la clé API' : 'Générer la clé API'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Affichage de la clé API */}
      {siteInfo && (
        <Card title="Clé API et Documentation">
          <Space direction="vertical" className="w-full" size="large">
            {/* Clé API */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Text strong>Clé API :</Text>
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? 'Masquer' : 'Afficher'}
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(siteInfo.apiKey, 'Clé API')}
                  >
                    Copier
                  </Button>
                </Space>
              </div>
              <Input
                value={showApiKey ? siteInfo.apiKey : maskKey(siteInfo.apiKey)}
                readOnly
                className="font-mono"
              />
            </div>

            {/* URL de base */}
            <div className="bg-gray-50 p-4 rounded">
              <Text strong>URL de base de l'API :</Text>
              <br />
              <Text code className="text-blue-600">{`${process.env.REACT_APP_API_URL}/api/site`}</Text>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(`${process.env.REACT_APP_API_URL}/api/site`, 'URL de base')}
                className="ml-2"
              >
                Copier
              </Button>
            </div>

            {/* Documentation API */}
            <div className="space-y-4">
              <Title level={4}>Endpoints disponibles :</Title>
              
              <div className="space-y-3">
                <div className="bg-green-50 p-3 rounded border-l-4 border-green-400">
                  <Text strong className="text-green-700">Lire les données :</Text>
                  <div className="mt-2 space-y-1">
                    <div><Text code>GET /products</Text> - Liste des produits</div>
                    <div><Text code>GET /products/:id</Text> - Détails d'un produit</div>
                    <div><Text code>GET /categories</Text> - Liste des catégories</div>
                    <div><Text code>GET /search?q=terme</Text> - Recherche produits</div>
                  </div>
                </div>

                <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                  <Text strong className="text-blue-700">Créer des commandes :</Text>
                  <div className="mt-2">
                    <div><Text code>POST /orders</Text> - Créer une commande</div>
                  </div>
                </div>
              </div>

              {/* Exemple de requête */}
              <div>
                <Text strong>Exemple de requête :</Text>
                <TextArea
                  readOnly
                  autoSize
                  value={`// Récupérer les produits
fetch('${process.env.REACT_APP_API_URL}/api/site/products', {
  headers: {
    'X-Site-API-Key': '${showApiKey ? siteInfo.apiKey : maskKey(siteInfo.apiKey)}',
    'Content-Type': 'application/json'
  }
})

// Créer une commande
fetch('${process.env.REACT_APP_API_URL}/api/site/orders', {
  method: 'POST',
  headers: {
    'X-Site-API-Key': '${showApiKey ? siteInfo.apiKey : maskKey(siteInfo.apiKey)}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    customer: {
      name: "Jean Dupont",
      phone: "+33123456789",
      email: "jean@email.com"
    },
    items: [
      {
        product_id: "123",
        quantity: 2,
        unit_price: 29.99
      }
    ],
    delivery_address: "123 Rue de la Paix, Paris"
  })
})`}
                  className="font-mono text-xs"
                />
              </div>
            </div>

            {/* Sécurité */}
            <Alert
              message="Sécurité importante"
              description={
                <div>
                  <p>• Gardez votre clé API confidentielle</p>
                  <p>• Utilisez HTTPS pour toutes vos requêtes</p>
                  <p>• Incluez toujours le header <Text code>X-Site-API-Key</Text></p>
                  <p>• Les données sont filtrées par votre compte automatiquement</p>
                </div>
              }
              type="warning"
              showIcon
            />
          </Space>
        </Card>
      )}
    </div>
  );
};

export default CustomStoreSettings;